import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { Brain, Send, Sparkles } from 'lucide-react';

// CoachSidebar component - Always visible sidebar with Coach IA chat

const Sidebar = styled.aside`
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  display: none;
`;

const CoachAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, var(--color-secondary), var(--color-secondary-dark));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
`;

const CoachInfo = styled.div`
  flex: 1;
  h3 {
    font-size: ${theme.fontSize.base};
    font-weight: ${theme.fontWeight.semibold};
    color: var(--color-primary);
    margin: 0 0 ${theme.spacing.xs} 0;
    font-family: 'Inter', sans-serif;
  }
  p {
    font-size: ${theme.fontSize.xs};
    color: var(--color-text-muted);
    margin: 0;
    font-family: 'Roboto', sans-serif;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Message = styled.div<{ fromCoach: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  flex-direction: ${({ fromCoach }) => fromCoach ? 'row' : 'row-reverse'};
`;

const MessageBubble = styled.div<{ fromCoach: boolean }>`
  background: ${({ fromCoach }) => fromCoach ? 'var(--color-surface)' : 'var(--color-primary)'};
  color: ${({ fromCoach }) => fromCoach ? 'var(--color-text)' : 'var(--color-white)'};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.xl};
  max-width: 80%;
  font-size: ${theme.fontSize.sm};
  line-height: 1.6;
  font-family: 'Roboto', sans-serif;
  transition: background-color 0.3s ease, color 0.3s ease;
  ${({ fromCoach }) => fromCoach 
    ? `border-top-left-radius: ${theme.borderRadius.sm};`
    : `border-top-right-radius: ${theme.borderRadius.sm};`
  }
`;

const MessageAvatar = styled.div<{ fromCoach: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ fromCoach }) => fromCoach ? 'linear-gradient(135deg, var(--color-secondary), var(--color-secondary-dark))' : 'var(--color-border)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ fromCoach }) => fromCoach ? 'var(--color-white)' : 'var(--color-text)'};
  flex-shrink: 0;
`;

const InputArea = styled.div`
  padding: ${theme.spacing.lg};
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: ${theme.spacing.sm};
  transition: border-color 0.3s ease;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.md};
  border: 1px solid var(--color-border);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSize.sm};
  font-family: 'Roboto', sans-serif;
  background: var(--color-surface);
  color: var(--color-text);
  transition: all 0.3s ease;
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 96, 100, 0.1);
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: var(--color-primary-dark);
    transform: scale(1.05);
  }
`;

const QuickActions = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  transition: border-color 0.3s ease;
`;

const QuickAction = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSize.sm};
  color: var(--color-text);
  font-family: 'Roboto', sans-serif;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
  }
`;

const CoachSidebar: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { id: 1, fromCoach: true, text: 'Olá! Sou seu Coach IA. Estou aqui para apoiá-lo em cada passo da sua jornada fitness. Como posso ajudá-lo hoje?' }
  ]);
  const [inputValue, setInputValue] = useState('');

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      fromCoach: false,
      text: inputValue
    };

    setMessages([...messages, userMessage]);
    setInputValue('');

    setTimeout(() => {
      const coachResponse = {
        id: messages.length + 2,
        fromCoach: true,
        text: 'Entendi! Vou analisar suas informações e te dar uma resposta personalizada em breve.'
      };
      setMessages(prev => [...prev, coachResponse]);
    }, 1000);
  };

  const quickActions = [
    { label: 'Ver meu treino de hoje', action: () => navigate('/') },
    { label: 'Ver meu progresso', action: () => navigate('/progress') },
    { label: 'Gerenciar metas', action: () => navigate('/goals') },
    { label: 'Explorar exercícios', action: () => navigate('/exercises') },
  ];

  return (
    <Sidebar>
      <SidebarHeader>
        <CoachAvatar>
          <Brain size={24} />
        </CoachAvatar>
        <CoachInfo>
          <h3>Coach IA</h3>
          <p>Seu parceiro de treino</p>
        </CoachInfo>
      </SidebarHeader>

      <MessagesArea>
        {messages.map((message) => (
          <Message key={message.id} fromCoach={message.fromCoach}>
            <MessageAvatar fromCoach={message.fromCoach}>
              {message.fromCoach ? <Brain size={16} /> : <Sparkles size={16} />}
            </MessageAvatar>
            <MessageBubble fromCoach={message.fromCoach}>
              {message.text}
            </MessageBubble>
          </Message>
        ))}
      </MessagesArea>

      <QuickActions>
        {quickActions.map((action, index) => (
          <QuickAction key={index} onClick={action.action}>
            {action.label}
          </QuickAction>
        ))}
      </QuickActions>

      <form onSubmit={handleSend}>
        <InputArea>
          <MessageInput
            type="text"
            placeholder="Digite sua mensagem..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <SendButton type="submit">
            <Send size={18} />
          </SendButton>
        </InputArea>
      </form>
    </Sidebar>
  );
};

export { CoachSidebar };
export default CoachSidebar;

